import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { toHex, utf8 } from './bytes';

/**
 * The browser half of the vault's wire crypto. Ported from
 * `shared/.../vault/cloud/VaultCrypto.kt` in the app repo; every constant
 * here is part of a format two implementations have to agree on, which is
 * what `crypto.vectors.test.ts` and its Kotlin twin exist to hold.
 *
 * Sealed layout: [suiteId:1][nonce:24][ciphertext||tag:16].
 */

export const KEY_BYTES = 32;
const NONCE_BYTES = 24;
const TAG_BYTES = 16;
const MIN_SEALED_BYTES = 1 + NONCE_BYTES + TAG_BYTES;

/** Agility hook: a future cipher is a new id plus lazy re-encrypt. */
export const SUITE_XCHACHA20_POLY1305 = 1;

const PATH_KEY_INFO = 'pondering.pathKey.v1';
const DATA_KEY_WRAP_AAD = utf8('pondering.dataKeyWrap.v1');

export function generateKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(KEY_BYTES));
}

export function seal(key: Uint8Array, aad: Uint8Array, plaintext: Uint8Array): Uint8Array {
  requireKey(key);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const body = xchacha20poly1305(key, nonce, aad).encrypt(plaintext);
  const out = new Uint8Array(1 + NONCE_BYTES + body.length);
  out[0] = SUITE_XCHACHA20_POLY1305;
  out.set(nonce, 1);
  out.set(body, 1 + NONCE_BYTES);
  return out;
}

/** Null on unknown suite, truncation, tamper, wrong key, or AAD mismatch. */
export function open(key: Uint8Array, aad: Uint8Array, sealed: Uint8Array): Uint8Array | null {
  requireKey(key);
  if (sealed.length < MIN_SEALED_BYTES) return null;
  if (sealed[0] !== SUITE_XCHACHA20_POLY1305) return null;
  const nonce = sealed.subarray(1, 1 + NONCE_BYTES);
  const body = sealed.subarray(1 + NONCE_BYTES);
  try {
    return xchacha20poly1305(key, nonce, aad).decrypt(body);
  } catch {
    return null;
  }
}

/** Binds a blob to its slot AND version — no cross-slot swapping, no
 *  per-file rollback to an older validly-encrypted version. */
export function blobAad(blobId: string, version: number): Uint8Array {
  return utf8(`${blobId}:${version}`);
}

export function derivePathKey(dataKey: Uint8Array): Uint8Array {
  // Salt is 32 zero bytes, matching the Kotlin default. RFC 5869 says an
  // absent salt means HashLen zeros, so this is the same value stated
  // outright rather than inherited from a library default.
  return hkdf(sha256, dataKey, new Uint8Array(32), utf8(PATH_KEY_INFO), KEY_BYTES);
}

/** Stable, path-hiding server-side blob name. v1 manifests only — the id
 *  moves with the path, so an edit overwrites the key the manifest points at. */
export function blobIdFor(pathKey: Uint8Array, relativePath: string): string {
  return toHex(hmac(sha256, pathKey, utf8(relativePath)));
}

/** Content-addressed blob name (v2 manifests): the same content always lands
 *  on the same key, so an upload can never overwrite a key the manifest still
 *  points at, a move is a manifest-only edit, and identical content dedups.
 *  NUL-prefixed so no relative path can collide with it in the HMAC domain —
 *  must stay byte-identical to `VaultCrypto.blobIdForContent`. */
export function blobIdForContent(pathKey: Uint8Array, contentHashHex: string): string {
  return toHex(hmac(sha256, pathKey, utf8(`\u0000content:${contentHashHex}`)));
}

export function wrapDataKey(masterKey: Uint8Array, dataKey: Uint8Array): Uint8Array {
  return seal(masterKey, DATA_KEY_WRAP_AAD, dataKey);
}

export function unwrapDataKey(masterKey: Uint8Array, wrapped: Uint8Array): Uint8Array | null {
  return open(masterKey, DATA_KEY_WRAP_AAD, wrapped);
}

export function contentHash(plaintext: Uint8Array): string {
  return toHex(sha256(plaintext));
}

function requireKey(key: Uint8Array): void {
  if (key.length !== KEY_BYTES) throw new Error(`key must be ${KEY_BYTES} bytes`);
}

export { TAG_BYTES, NONCE_BYTES };
